import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Globe } from 'lucide-react';
import FlagIcon from './FlagIcon';
import { VOICE_THEME as t } from '../../utils/voiceMetadata';

/**
 * LanguageFilterDropdown — dropdown fino de FILTRO de idioma da lista lateral
 * de vozes. Mesmo padrão visual do LanguagePreviewDropdown (portal, bandeiras,
 * linhas compactas), mas genérico: recebe opções prontas e devolve o value.
 *
 * Props:
 *   - options: Array<{ value, label, flag? }> — flag = código ISO 3166 (ex.: 'br');
 *     sem flag (opção "Todos os idiomas") mostra o ícone de globo.
 *   - value: string
 *   - onChange(value)
 */
export default function LanguageFilterDropdown({ options = [], value, onChange }) {
  const [open, setOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((o) => o.value === value) || options[0] || null;

  const updateRect = useCallback(() => {
    if (!buttonRef.current) return;
    setButtonRect(buttonRef.current.getBoundingClientRect());
  }, []);

  // ESC + click fora + scroll externo fecham (scroll interno do menu não fecha)
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      const insideButton = wrapperRef.current && wrapperRef.current.contains(e.target);
      const insideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!insideButton && !insideMenu) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => updateRect();

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updateRect]);

  const toggleOpen = () => {
    if (open) setOpen(false);
    else { updateRect(); setOpen(true); }
  };

  const handleSelect = (opt) => {
    setOpen(false);
    if (onChange) onChange(opt.value);
  };

  const menuStyles = buttonRect ? computeMenuPosition(buttonRect) : null;

  const renderIcon = (opt, size) => (
    opt && opt.flag
      ? <FlagIcon code={opt.flag} size={size} rounded />
      : <Globe size={parseInt(size, 10)} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
  );

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          padding: '5px 10px',
          borderRadius: '8px',
          border: open ? '1px solid var(--dp-blue-500)' : '1px solid var(--dp-border-subtle)',
          background: open
            ? 'linear-gradient(135deg, rgba(13,174,255,0.10), rgba(13,174,255,0.04))'
            : 'var(--dp-bg-input, var(--dp-bg-primary))',
          color: 'var(--dp-text-primary)',
          fontSize: '11px',
          fontFamily: 'var(--dp-font)',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.18s',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
        title={selected ? selected.label : ''}
      >
        {renderIcon(selected, '14px')}
        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected ? selected.label : ''}
        </span>
        <ChevronDown
          size={12}
          style={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            opacity: 0.7,
            flexShrink: 0,
          }}
        />
      </button>

      {open && menuStyles && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: `${menuStyles.top}px`,
            left: `${menuStyles.left}px`,
            width: `${menuStyles.width}px`,
            maxHeight: '300px',
            overflowY: 'auto',
            background: t.bg,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${t.border}`,
            borderRadius: '10px',
            boxShadow: t.shadow,
            zIndex: 1300,
            padding: '4px',
            fontFamily: 'var(--dp-font)',
            animation: 'agFadeInUp 0.15s ease-out',
            color: t.text,
          }}
          className="ag-scrollbar"
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt) => {
            const isCurrent = selected && opt.value === selected.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => handleSelect(opt)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '5px 8px',
                  borderRadius: '6px',
                  border: 'none',
                  background: isCurrent ? 'rgba(13,174,255,0.12)' : 'transparent',
                  color: isCurrent ? 'var(--dp-blue-500)' : t.text,
                  cursor: 'pointer',
                  fontSize: '11.5px',
                  fontFamily: 'var(--dp-font)',
                  fontWeight: isCurrent ? 600 : 400,
                  textAlign: 'left',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = t.hover; }}
                onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: '14px', display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                  {isCurrent && <Check size={12} />}
                </span>
                {renderIcon(opt, '14px')}
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Posição do menu em viewport: largura do botão (mín 200), flip vertical
 * quando não cabe embaixo, clamp horizontal.
 */
function computeMenuPosition(rect) {
  const PAD = 8;
  const GAP = 4;
  const MIN_WIDTH = 200;
  const MAX_WIDTH = 320;
  const MENU_HEIGHT_ESTIMATE = 300;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let width = Math.min(MAX_WIDTH, Math.max(rect.width, MIN_WIDTH));
  let left = rect.left;
  if (left + width + PAD > viewportW) left = Math.max(PAD, rect.right - width);
  if (left < PAD) left = PAD;

  let top = rect.bottom + GAP;
  const spaceBelow = viewportH - rect.bottom - PAD;
  const spaceAbove = rect.top - PAD;
  if (spaceBelow < 180 && spaceAbove > spaceBelow) {
    top = Math.max(PAD, rect.top - GAP - Math.min(MENU_HEIGHT_ESTIMATE, spaceAbove));
  }
  return { top, left, width };
}
